export function getCurrentRegionId() {
  return process.env.NEXT_PUBLIC_REGION || "wellington";
}

const REGION_DOMAINS = {
  wellington: "catchy.nz",
  chch: "chch.catchy.nz",
  otago: "otago.catchy.nz",
  auckland: "auckland.catchy.nz",
};

const REGION_DEV_PORTS = {
  wellington: 3030,
  chch: 3031,
  otago: 3032,
  auckland: 3033,
};

// This is intentionally presentation-adjacent configuration rather than a
// derivation from provider capabilities. A region can have ferry data, for
// example, without exposing a ferry as a top-level app mode.
const MODE_NAVIGATION_ITEMS = {
  wellington: [
    { id: "bus", label: "Bus", href: "/", mode: "bus" },
    { id: "train", label: "Train", href: "/train", mode: "train" },
    {
      id: "cablecar",
      label: "Cable Car",
      href: "/cable-car",
      mode: "cable_car",
    },
    { id: "ferry", label: "Ferry", href: "/ferry", mode: "ferry" },
  ],
  chch: [{ id: "bus", label: "Bus", href: "/", mode: "bus" }],
  otago: [{ id: "bus", label: "Bus", href: "/", mode: "bus" }],
  auckland: [
    { id: "bus", label: "Bus", href: "/", mode: "bus" },
    { id: "train", label: "Train", href: "/train", mode: "train" },
    { id: "ferry", label: "Ferry", href: "/ferry", mode: "ferry" },
  ],
};

function normalizeOrigin(value) {
  const origin = String(value || "").trim();

  if (!origin) {
    return null;
  }

  const withProtocol = /^https?:\/\//i.test(origin)
    ? origin
    : `https://${origin}`;

  try {
    return new URL(withProtocol).origin;
  } catch {
    return null;
  }
}

const REGION_STOP_TILEJSON_ENV = {
  wellington: process.env.NEXT_PUBLIC_WELLINGTON_STOP_TILEJSON_URL || "",
  chch: process.env.NEXT_PUBLIC_CHCH_STOP_TILEJSON_URL || "",
  otago: process.env.NEXT_PUBLIC_OTAGO_STOP_TILEJSON_URL || "",
  auckland: process.env.NEXT_PUBLIC_AUCKLAND_STOP_TILEJSON_URL || "",
};

function readStopTileJsonUrl(regionId) {
  const regionScoped = REGION_STOP_TILEJSON_ENV[regionId] || "";
  const shared = process.env.NEXT_PUBLIC_STOP_TILEJSON_URL || "";
  const value = String(regionScoped || shared).trim();

  return value || null;
}

const MAP_STOP_LAYERS = {
  wellington: {
    stops: "wellington-stops",
    activeStops: "wellington-stops-active",
  },
  chch: {
    stops: "christchurch-stops",
    activeStops: "christchurch-stops-active",
  },
  otago: {
    stops: "otago-stops",
    activeStops: "otago-stops-active",
  },
  auckland: {
    stops: "auckland-stops",
    activeStops: "auckland-stops-active",
  },
};

const DEFAULT_STOP_TILE_LAYER = "stops";
const MAP_STOP_SOURCE_IDS = {
  wellington: "catchy-wellington-stops",
  chch: "catchy-chch-stops",
  otago: "catchy-otago-stops",
  auckland: "catchy-auckland-stops",
};

function buildStopSourceConfig(regionId) {
  const tileJsonUrl = readStopTileJsonUrl(regionId);

  if (!tileJsonUrl) {
    return null;
  }

  return {
    runtime: true,
    sourceId: MAP_STOP_SOURCE_IDS[regionId],
    type: "vector",
    url: tileJsonUrl,
    sourceLayer: DEFAULT_STOP_TILE_LAYER,
    promoteId: "stop_id",
  };
}

const MAP_STOP_SOURCES = {
  wellington: buildStopSourceConfig("wellington"),
  chch: buildStopSourceConfig("chch"),
  otago: buildStopSourceConfig("otago"),
  auckland: buildStopSourceConfig("auckland"),
};

const MAP_PAGE_METADATA = {
  wellington: {
    title: "Wellington public transport map and live trips | Catchy",
    description:
      "Find Wellington bus, train, ferry, and cable car stops, plus active public transport trips near you.",
  },
  chch: {
    title: "Christchurch Metro map and live trips | Catchy",
    description:
      "Find Christchurch Metro bus stops, Diamond Harbour ferry stops, and active public transport trips near you.",
  },
  otago: {
    title: "Otago public transport map and live trips | Catchy",
    description:
      "Find Orbus stops and active public transport trips across Dunedin, Queenstown, and Otago.",
  },
  auckland: {
    title: "Auckland public transport map and live trips | Catchy",
    description:
      "Find Auckland public transport stops and active trips near you.",
  },
};

const STOP_TYPE_BACK_HREFS = {
  default: {
    bus: "/",
    train: "/",
    cable_car: "/",
    ferry: "/",
  },
  wellington: {
    bus: "/",
    train: "/train",
    cable_car: "/cable-car",
    ferry: "/ferry",
  },
};

function normalizeStopType(value) {
  return value === "cableCar" ? "cable_car" : value || "bus";
}

function getRegionScopedValue(valuesByRegion, regionId = getCurrentRegionId()) {
  return valuesByRegion[regionId] || valuesByRegion.default;
}

export function isRegion(regionId) {
  return getCurrentRegionId() === regionId;
}

export function getModeNavigationItems(regionId = getCurrentRegionId()) {
  return MODE_NAVIGATION_ITEMS[regionId] || MODE_NAVIGATION_ITEMS.wellington;
}

export function getRegionDomain() {
  return REGION_DOMAINS[getCurrentRegionId()] || REGION_DOMAINS.wellington;
}

export function getCanonicalSiteUrl() {
  return `https://${getRegionDomain()}`;
}

export function getPublicSiteUrl() {
  const explicitOrigin = normalizeOrigin(process.env.NEXT_PUBLIC_SITE_URL);

  if (explicitOrigin) {
    return explicitOrigin;
  }

  if (process.env.NODE_ENV === "development") {
    const regionId = getCurrentRegionId();
    const port =
      process.env.NEXT_PUBLIC_DEV_PORT || REGION_DEV_PORTS[regionId] || 3000;

    return `http://localhost:${port}`;
  }

  return getCanonicalSiteUrl();
}

const KNOWN_REGION_IDS = ["wellington", "chch", "otago", "auckland"];
const STORAGE_MIGRATION_VERSION = 4;
const DEFAULT_MIGRATABLE_STORAGE_KEYS = [
  "favourites",
  "last_main",
  "recents",
  "destinations",
  "preferred_directions",
  "peek",
  "bus_details",
  "show_recent_departures",
  "preferred_services",
  "preferred_stop_names",
  "prefer_satellite",
  "dismissed_alerts",
  "dismissed_alerts_last_cleaned",
  "map_center",
  "show_vehicles",
  "prefer_user_location",
  "snapper_card",
];
const STOP_LINKED_STORAGE_KEYS = new Set([
  "favourites",
  "recents",
  "destinations",
  "preferred_directions",
  "peek",
  "preferred_services",
  "preferred_stop_names",
]);

function stripKnownStoragePrefix(key) {
  for (const regionId of KNOWN_REGION_IDS) {
    const canonicalPrefix = `catchy_${regionId}_`;
    if (key.startsWith(canonicalPrefix)) {
      return key.slice(canonicalPrefix.length);
    }

    const legacyPrefix = `${regionId}_`;
    if (key.startsWith(legacyPrefix)) {
      return key.slice(legacyPrefix.length);
    }
  }

  return key;
}

export function getStorageKey(baseKey) {
  const regionId = getCurrentRegionId();
  const normalizedBaseKey = stripKnownStoragePrefix(baseKey);
  return `catchy_${regionId}_${normalizedBaseKey}`;
}

function getLegacyStorageKeyAliases(baseKey) {
  const regionId = getCurrentRegionId();
  const normalizedBaseKey = stripKnownStoragePrefix(baseKey);
  const aliases =
    regionId === "wellington"
      ? [normalizedBaseKey, `wellington_${normalizedBaseKey}`]
      : [`${regionId}_${normalizedBaseKey}`];

  if (normalizedBaseKey === "prefer_user_location") {
    return Array.from(
      new Set([
        `catchy_${regionId}_preferUserLocation`,
        `${regionId}_preferUserLocation`,
        ...(regionId === "wellington" ? ["preferUserLocation"] : []),
        ...aliases,
      ]),
    );
  }

  return Array.from(new Set(aliases));
}

function shouldPreferLegacyAlias(aliasKey) {
  const regionId = getCurrentRegionId();

  if (regionId === "wellington") {
    return false;
  }

  return aliasKey.startsWith(`${regionId}_`);
}

function hasStoredMigrationValue(value) {
  return value !== null && value !== "undefined";
}

function migrateLegacyStopReferenceShape(value) {
  // Temporary storage-shape migration for legacy records that used `sms`
  // instead of `stop_id`. Remove after the new shape has baked for a few months.
  if (Array.isArray(value)) {
    let changed = false;
    const next = value.map((entry) => {
      const migrated = migrateLegacyStopReferenceShape(entry);
      if (migrated !== entry) {
        changed = true;
      }
      return migrated;
    });
    return changed ? next : value;
  }

  if (!value || typeof value !== "object") {
    return value;
  }

  let changed = false;
  const next = {};

  for (const [key, childValue] of Object.entries(value)) {
    if (key === "sms") {
      changed = true;
      if (!("stop_id" in value)) {
        next.stop_id = childValue;
      }
      continue;
    }

    const migrated = migrateLegacyStopReferenceShape(childValue);
    if (migrated !== childValue) {
      changed = true;
    }
    next[key] = migrated;
  }

  if ("stop_id" in value) {
    next.stop_id = value.stop_id;
  }

  return changed ? next : value;
}

export function migrateLegacyLocalStorageKeys(additionalBaseKeys = []) {
  if (typeof window === "undefined") {
    return;
  }

  const regionId = getCurrentRegionId();
  const markerKey = `__catchy_storage_migrated_${regionId}_v${STORAGE_MIGRATION_VERSION}`;

  if (window.sessionStorage.getItem(markerKey) === "true") {
    return;
  }

  const baseKeys = Array.from(
    new Set([...DEFAULT_MIGRATABLE_STORAGE_KEYS, ...additionalBaseKeys]),
  );

  for (const baseKey of baseKeys) {
    const canonicalKey = getStorageKey(baseKey);
    let canonicalValue = window.localStorage.getItem(canonicalKey);
    const aliasKeys = getLegacyStorageKeyAliases(baseKey);
    const normalizedBaseKey = stripKnownStoragePrefix(baseKey);

    for (const aliasKey of aliasKeys) {
      if (aliasKey === canonicalKey) continue;

      const aliasValue = window.localStorage.getItem(aliasKey);
      if (!hasStoredMigrationValue(aliasValue)) continue;

      if (
        !hasStoredMigrationValue(canonicalValue) ||
        (normalizedBaseKey !== "prefer_user_location" &&
          shouldPreferLegacyAlias(aliasKey))
      ) {
        try {
          window.localStorage.setItem(canonicalKey, aliasValue);
          canonicalValue = window.localStorage.getItem(canonicalKey);
        } catch {
          canonicalValue = null;
        }
      }

      break;
    }

    if (hasStoredMigrationValue(canonicalValue)) {
      for (const aliasKey of aliasKeys) {
        if (aliasKey === canonicalKey) continue;
        window.localStorage.removeItem(aliasKey);
      }
    }

    if (
      STOP_LINKED_STORAGE_KEYS.has(stripKnownStoragePrefix(baseKey)) &&
      canonicalValue !== null &&
      canonicalValue !== "undefined"
    ) {
      try {
        const parsed = JSON.parse(canonicalValue);
        const migrated = migrateLegacyStopReferenceShape(parsed);
        if (migrated !== parsed) {
          canonicalValue = JSON.stringify(migrated);
          window.localStorage.setItem(canonicalKey, canonicalValue);
        }
      } catch {
        // Leave invalid JSON alone; useLocalStorage will fall back safely.
      }
    }
  }

  window.sessionStorage.setItem(markerKey, "true");
}

export function getMapCenterDefault() {
  switch (getCurrentRegionId()) {
    case "chch":
      return { center: { lng: 172.6362, lat: -43.5321 }, zoom: 14 };
    case "otago":
      return { center: { lng: 170.5028, lat: -45.8788 }, zoom: 14 };
    case "auckland":
      return { center: { lng: 174.7633, lat: -36.8485 }, zoom: 14 };
    default:
      return { center: { lng: 174.7743, lat: -41.2883 }, zoom: 14 };
  }
}

export function getMapStopLayerIds() {
  return MAP_STOP_LAYERS[getCurrentRegionId()] || MAP_STOP_LAYERS.wellington;
}

export function getMapStopSourceConfig() {
  return MAP_STOP_SOURCES[getCurrentRegionId()] || null;
}

export function getMapPageMetadata() {
  return (
    MAP_PAGE_METADATA[getCurrentRegionId()] || MAP_PAGE_METADATA.wellington
  );
}

export function getBackHrefForStopType(stopType, regionId = getCurrentRegionId()) {
  const hrefs = getRegionScopedValue(STOP_TYPE_BACK_HREFS, regionId);
  return hrefs[normalizeStopType(stopType)] || hrefs.bus || "/";
}

export function getMapOriginHref(from, regionId = getCurrentRegionId()) {
  switch (from) {
    case "bus":
      return getBackHrefForStopType("bus", regionId);
    case "train":
      return getBackHrefForStopType("train", regionId);
    case "cable-car":
      return getBackHrefForStopType("cable_car", regionId);
    case "ferry":
      return getBackHrefForStopType("ferry", regionId);
    default:
      return "/";
  }
}
